import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { algoliasearch, SearchClient } from 'algoliasearch';
import instantsearch, { InstantSearch } from 'instantsearch.js';
import historyRouter from 'instantsearch.js/es/lib/routers/history';
import { BehaviorSubject, concatMap, filter, interval, map, Subject, Subscription, take, takeUntil } from 'rxjs';
import { IAlgoliaSearchClientSettings } from '../../../../shared/models/algolia-search-client-settings';
import { AuthService } from '../../../../shared/services/auth.service';
import { ConfigurationService } from '../../../../shared/services/configuration.service';

@Injectable({
  providedIn: 'root'
})

export class InstantSearchService implements OnDestroy {
  private searchClient?: SearchClient;
  private searchInstance?: InstantSearch;
  private instantSearchRouter = historyRouter();

  private subscriptions = new Subscription();
  private refreshTimer$ = new Subject<void>();
  instanceSubject$: BehaviorSubject<InstantSearch | null>  =  new BehaviorSubject<InstantSearch | null>(null);

  get client(): SearchClient | undefined {
    return this.searchClient;
  }

  get instance(): InstantSearch | undefined {
    return this.searchInstance;
  }


  constructor(
    private configurationService: ConfigurationService,
    private authService: AuthService
  ) {
    this.initialise();
    this.schedulePeriodicInitialise();  // Refresh the SearchClient token every hour
  }

  ngOnDestroy(): void {
    console.log("InstantSearchService: onDestroy.")
    this.refreshTimer$.next();
    this.refreshTimer$.complete();
    this.subscriptions.unsubscribe();
  }

  /** Initializes Algolia InstantSearch if feature is enabled */
  private initialise(): void {
    this.subscriptions.add(
      this.authService.currentUserSubject.pipe(
        filter(user => !!user),
        concatMap(() => this.configurationService.getAppFeaturesSettings()),
        map(response => this.isAlgoliaEnabled(response)),
        filter(enabled => enabled),
        concatMap(() => this.configurationService.getAlgoliaSearchClientSettings()),
        take(1)
      ).subscribe({
        next: response => {
          if (response.success) {
            this.setupSearch(response.content);
          } else {
            console.error('Failed to load Algolia settings:', response.message);
          }
        },
        error: err => console.error('Algolia initialization error:', err)
      })
    );
  }

  private isAlgoliaEnabled(response: any): boolean {
    if (response?.content && response.success) {
      return response.content.algoliaSearch;
    }
    console.error('Failed to load app features settings:', response?.message ?? 'No response');
    return false;
  }

  private schedulePeriodicInitialise(): void {
    // Re-initialise the Algolia Search client every hour because the token is valid for one hour only.
    interval(60 * 60 * 1000)
      .pipe(takeUntil(this.refreshTimer$))
      .subscribe(() => {

        this.initialise();
        console.log("InstantSearchService: Refreshed token.");
      });
  }

  /** Sets up Algolia InstantSearch client and instance */
  private setupSearch(settings: IAlgoliaSearchClientSettings): void {
    if (this.searchInstance)
    {
      this.searchInstance.dispose();
    }

    this.searchClient = algoliasearch(settings.applicationId, settings.searchApiKey);
    this.searchInstance = instantsearch({
      indexName: settings.indexName,
      searchClient: this.searchClient,
      routing: { router: this.instantSearchRouter },
      future: { preserveSharedStateOnUnmount: true }
    });

    this.searchInstance.start();
    this.instanceSubject$.next(this.searchInstance);
  }

}
